import axios from 'axios';
import { logger } from '../utils/logger';

const TAX_API_BASE_URL = 'https://book.securereservation.org/api';

/**
 * Tax API Service
 * Handles tax calculation for bookings
 */

/**
 * Is this tax ALREADY included in the room rate? (TaxType === "Included")
 * Included taxes must NOT be added on top of the rate again — they are display-only.
 * ONLY an explicit "included" counts; empty/"Excluded" stay add-on (existing behaviour
 * untouched). Used to skip included taxes in every summation/display loop.
 * @param {Object} tax
 * @returns {boolean}
 */
export const isTaxIncluded = (tax) => {
  const t = (tax?.TaxType ?? tax?.taxType ?? '').toString().trim().toLowerCase();
  return t === 'included';
};

/**
 * Does this rate plan have ANY included-in-rate tax? If so the UI shows
 * "Price includes all taxes and fees" instead of an add-on tax line.
 * @param {Array} taxes - ratePlan.taxes
 * @returns {boolean}
 */
export const hasIncludedTax = (taxes) => Array.isArray(taxes) && taxes.some(isTaxIncluded);

/**
 * Get tax calculation from API
 * @param {Object} taxParams - Tax calculation parameters
 * @param {number} taxParams.nights - Number of nights
 * @param {number} taxParams.conversionrate - Currency conversion rate (default: 0)
 * @param {Object} taxParams.noofroomArray - Object with room quantities, e.g., {"0": 1, "1": 2}
 * @param {number} taxParams.totalnumberofbedrooms - Total number of bedrooms
 * @param {number} taxParams.price - Base price for tax calculation
 * @param {Array} taxParams.data - Array of tax objects from RatePlans -> Taxs -> Tax
 * @returns {Promise<Object>} Tax calculation response
 * 
 * @example
 * const taxData = await getTax({
 *   nights: 2,
 *   conversionrate: 0,
 *   noofroomArray: {"0": 1, "1": 2},
 *   totalnumberofbedrooms: 3,
 *   price: 10000,
 *   data: [
 *     {
 *       TaxTypeId: 1,
 *       TaxName: "GST",
 *       TaxType: "Included",
 *       TaxPriceMode: "Percentage",
 *       TaxValue: 12,
 *       TaxId: 3,
 *       TaxSorting: 1,
 *       TaxChargeTypeId: 1
 *     }
 *   ]
 * });
 */
export const getTax = async (taxParams) => {
  try {
    const {
      nights,
      conversionrate = 0,
      noofroomArray,
      totalnumberofbedrooms,
      price,
      data = []
    } = taxParams;

    // Validate required parameters
    if (!nights || nights < 1) {
      throw new Error('Number of nights is required and must be at least 1');
    }

    if (!price || price < 0) {
      throw new Error('Price is required and must be non-negative');
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error('Tax data array is required');
    }

    // Build query parameters
    const queryParams = new URLSearchParams({
      nights: nights.toString(),
      conversionrate: conversionrate.toString(),
      noofroomArray: JSON.stringify(noofroomArray || {}),
      totalnumberofbedrooms: (totalnumberofbedrooms || 0).toString(),
      price: price.toString(),
      data: JSON.stringify(data)
    });

    const url = `${TAX_API_BASE_URL}/get-tax?${queryParams.toString()}`;

    logger.debug('Tax API request', {
      url,
      nights,
      conversionrate,
      noofroomArray,
      totalnumberofbedrooms,
      price,
      dataCount: data.length
    });

    const response = await axios.get(url, {
      timeout: 30000,
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    logger.debug('Tax API response received');

    return response.data;
  } catch (error) {
    logger.error('Error fetching tax:', error.message || error);
    
    if (error.response) {
      // Server responded with error status
      throw new Error(
        error.response.data?.message || 
        error.response.data?.error || 
        `Tax calculation failed: ${error.response.status} ${error.response.statusText}`
      );
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error: Please check your internet connection');
    } else {
      // Error in request setup
      throw new Error(error.message || 'An error occurred during tax calculation');
    }
  }
};

/**
 * Extract tax data from rate plan
 * @param {Object} ratePlan - Rate plan object from availability API
 * @returns {Array} Array of tax objects formatted for tax API
 */
export const extractTaxDataFromRatePlan = (ratePlan) => {
  try {
    if (!ratePlan) {
      logger.warn('extractTaxDataFromRatePlan: ratePlan is null or undefined');
      return [];
    }
    
    // Tax data comes from RatePlans -> Taxs -> Tax
    // Check multiple possible paths for tax data
    let taxArray = [];
    
    // Try different paths: taxes (from availabilityApi transform), Taxs (from API), tax, Tax
    if (ratePlan.taxes && Array.isArray(ratePlan.taxes) && ratePlan.taxes.length > 0) {
      // Already transformed format from availabilityApi
      taxArray = ratePlan.taxes;
    } else if (ratePlan.Taxs) {
      // Raw API format: Taxs -> Tax
      const taxs = ratePlan.Taxs;
      taxArray = taxs.Tax || taxs.tax || [];
      if (!Array.isArray(taxArray)) {
        taxArray = taxArray ? [taxArray] : [];
      }
    } else if (ratePlan.tax) {
      taxArray = Array.isArray(ratePlan.tax) ? ratePlan.tax : [ratePlan.tax];
    } else if (ratePlan.Tax) {
      taxArray = Array.isArray(ratePlan.Tax) ? ratePlan.Tax : [ratePlan.Tax];
    }

    if (!taxArray || taxArray.length === 0) {
      logger.debug('No tax data found in rate plan');
      return [];
    }

    // Format tax data for API
    return taxArray.map(tax => {
      // Handle both object with @attributes and direct object
      const attrs = tax?.['@attributes'] || tax || {};
      
      const formattedTax = {
        TaxTypeId: parseInt(attrs.TaxTypeId || attrs.taxTypeId || 0) || 0,
        TaxName: attrs.TaxName || attrs.taxName || '',
        TaxType: attrs.TaxType || attrs.taxType || '',
        TaxPriceMode: attrs.TaxPriceMode || attrs.taxPriceMode || '',
        TaxValue: parseFloat(attrs.TaxValue || attrs.taxValue || 0) || 0,
        TaxId: parseInt(attrs.TaxId || attrs.taxId || 0) || 0,
        TaxSorting: parseInt(attrs.TaxSorting || attrs.taxSorting || 0) || 0,
        TaxChargeTypeId: parseInt(attrs.TaxChargeTypeId || attrs.taxChargeTypeId || 0) || 0
      };
      
      return formattedTax;
    });
  } catch (error) {
    logger.error('Error extracting tax data from rate plan:', error);
    return [];
  }
};

/**
 * Calculate total tax for selected rooms
 * @param {Array} selectedRooms - Array of selected room objects
 * @param {number} nights - Number of nights
 * @param {number} conversionrate - Currency conversion rate (default: 0)
 * @returns {Promise<Object>} Tax calculation result
 */
export const calculateTaxForRooms = async (selectedRooms, nights, conversionrate = 0) => {
  try {
    if (!selectedRooms || selectedRooms.length === 0) {
      return {
        totalTax: 0,
        taxBreakdown: [],
        taxInclusive: false
      };
    }

    // Build noofroomArray from selected rooms
    const noofroomArray = {};
    let totalBedrooms = 0;
    let totalPrice = 0;
    const allTaxData = [];

    selectedRooms.forEach((item, index) => {
      const roomId = item.room?.id || index;
      const quantity = item.quantity || 1;
      noofroomArray[index.toString()] = quantity;
      
      // Calculate total bedrooms
      const bedrooms = item.ratePlan?.noOfBedRooms || item.room?.bedrooms || 1;
      totalBedrooms += bedrooms * quantity;
      
      // Calculate total price
      const roomPrice = (item.room?.basePrice || 0) * (1 + (item.ratePlan?.priceModifier || 0));
      totalPrice += roomPrice;
      
      // Extract tax data from rate plan
      if (item.ratePlan) {
        const taxData = extractTaxDataFromRatePlan(item.ratePlan);
        // Merge tax data (avoid duplicates)
        taxData.forEach(tax => {
          const existing = allTaxData.find(t => t.TaxId === tax.TaxId);
          if (!existing) {
            allTaxData.push(tax);
          }
        });
      }
    });

    if (allTaxData.length === 0) {
      return {
        totalTax: 0,
        taxBreakdown: [],
        taxInclusive: false
      };
    }

    // Call tax API
    const taxResult = await getTax({
      nights,
      conversionrate,
      noofroomArray,
      totalnumberofbedrooms: totalBedrooms,
      price: totalPrice,
      data: allTaxData
    });

    return taxResult;
  } catch (error) {
    logger.error('Error calculating tax for rooms:', error.message || error);
    throw error;
  }
};

